import { MapPin, CalendarDays, BedDouble, Bath, Ruler, Waves, Sunset, Trees, Sun, Car, Sofa, Check, ExternalLink } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useAppData } from "../../context/AppDataContext";
import { formatPrice, relativeDate } from "../../lib/format";
import { Tag, InfoChip, SellerCard } from "./DetailParts";

function Head({ listing }) {
  const { t, lang } = useLang();
  const { zoneName } = useAppData();
  return (
    <>
      <h1 className="text-3xl font-extrabold leading-tight text-navy" data-testid="detail-title">{listing.title}</h1>
      <p className="mt-2 flex items-center gap-1.5 text-[15px] text-slate-600" data-testid="detail-zone-date">
        <MapPin className="h-4 w-4 text-alert" /> {zoneName(listing.zone)} · {relativeDate(listing.created_at, lang)}
      </p>
    </>
  );
}

function Description({ title, text }) {
  return (
    <>
      <h2 className="mt-6 text-lg font-extrabold text-navy">{title}</h2>
      <p className="mt-2 whitespace-pre-line break-words text-[15px] leading-relaxed text-slate-700" data-testid="detail-description">{text}</p>
    </>
  );
}

export function ContactosBody({ listing }) {
  const { t, lang } = useLang();
  const { zoneName, catName, optLabel } = useAppData();
  const a = listing.attributes;
  return (
    <div data-testid="contactos-detail">
      <h1 className="text-4xl font-extrabold leading-tight text-navy" data-testid="detail-title">{listing.title}</h1>
      <div className="mt-2 flex flex-wrap items-center gap-2.5 text-[15px] text-slate-600">
        <span className="flex items-center gap-1.5" data-testid="detail-zone-date"><MapPin className="h-4 w-4 text-alert" /> {zoneName(listing.zone)} · {relativeDate(listing.created_at, lang)}</span>
        <Tag solid testid="detail-category">{catName(listing.category)}</Tag>
      </div>
      <div className="mt-4 flex flex-wrap gap-2" data-testid="contactos-info-chips">
        {a.gender && <InfoChip testid="chip-gender">{optLabel("gender", a.gender)}</InfoChip>}
        {a.looking_for?.length > 0 && (
          <InfoChip testid="chip-looking-for">{t("contact.lookingFor", { v: a.looking_for.map((x) => optLabel("looking_for", x)).join(", ") })}</InfoChip>
        )}
        {a.languages?.length > 0 && (
          <InfoChip testid="chip-languages">{t("contact.languages", { v: a.languages.map((x) => optLabel("languages", x)).join(" · ") })}</InfoChip>
        )}
      </div>
      <Description title={t("contact.about")} text={listing.description} />
    </div>
  );
}

const HIGHLIGHTS = [["piscina", Waves], ["vistas_mar", Sunset], ["jardin", Trees], ["terraza", Sun], ["parking", Car], ["amueblada", Sofa]];

function FeatureBox({ icon: Icon, label, testid }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 rounded-2xl border border-slate-200 px-1 py-3 text-center" data-testid={testid}>
      <Icon className="h-6 w-6 text-navy" strokeWidth={1.6} />
      <span className="text-[13px] text-navy">{label}</span>
    </div>
  );
}

function ZoneMap({ zoneKey }) {
  const { t } = useLang();
  const { zones } = useAppData();
  const z = zones.find((x) => x.key === zoneKey);
  if (!z) return null;
  const d = zoneKey === "all" ? 0.25 : 0.018;
  const bbox = [z.lon - d * 1.6, z.lat - d, z.lon + d * 1.6, z.lat + d].join(",");
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${z.lat},${z.lon}`;
  const link = `https://www.openstreetmap.org/?mlat=${z.lat}&mlon=${z.lon}#map=14/${z.lat}/${z.lon}`;
  return (
    <div className="mt-6" data-testid="detail-zone-map">
      <h2 className="text-lg font-extrabold text-navy">{t("prop.location")}</h2>
      <div className="relative mt-2 h-44 overflow-hidden rounded-2xl ring-1 ring-slate-200">
        <iframe title="map" src={src} className="pointer-events-none h-full w-full border-0" loading="lazy" />
      </div>
      <a href={link} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-brand" data-testid="detail-map-link">
        {z.name} · {t("prop.openMap")} <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}

export function PropertyBody({ listing }) {
  const { t, lang } = useLang();
  const { zoneName, catName, optLabel } = useAppData();
  const a = listing.attributes;
  const feats = a.features || [];
  const hl = HIGHLIGHTS.find(([k]) => feats.includes(k));
  const boxes = [
    a.rooms != null && [BedDouble, t("prop.rooms", { n: a.rooms }), "box-rooms"],
    a.bathrooms != null && [Bath, t("prop.baths", { n: a.bathrooms }), "box-baths"],
    a.area != null && [Ruler, t("prop.area", { n: a.area }), "box-area"],
    hl && [hl[1], optLabel("features", hl[0]), "box-highlight"],
  ].filter(Boolean);
  const unit = a.price_unit && a.price_unit !== "total" ? t(`unit.${a.price_unit}`) : "";
  const price = formatPrice({ ...listing, attributes: {} }, t, lang);
  return (
    <div data-testid="property-detail">
      <div className="flex flex-wrap gap-2">
        {a.operation && <Tag solid testid="detail-operation">{optLabel("operation", a.operation)}</Tag>}
        <Tag testid="detail-category">{catName(listing.category)}</Tag>
      </div>
      <h1 className="mt-3 text-[28px] font-extrabold leading-tight text-navy" data-testid="detail-title">{listing.title}</h1>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-2">
        <p className="text-3xl font-extrabold text-brand" data-testid="detail-price">
          {price}{unit && listing.price ? <span className="ml-1 text-lg font-semibold">{unit}</span> : null}
        </p>
        <p className="flex items-center gap-1.5 text-sm text-slate-600" data-testid="detail-zone-date">
          <MapPin className="h-4 w-4 text-alert" /> {zoneName(listing.zone)} · {relativeDate(listing.created_at, lang)}
        </p>
      </div>
      {boxes.length > 0 && (
        <div className={`mt-4 grid gap-2.5 grid-cols-${boxes.length === 4 ? 4 : boxes.length}`} style={{ gridTemplateColumns: `repeat(${boxes.length}, minmax(0, 1fr))` }} data-testid="detail-feature-boxes">
          {boxes.map(([icon, label, id]) => <FeatureBox key={id} icon={icon} label={label} testid={id} />)}
        </div>
      )}
      <Description title={t("detail.description")} text={listing.description} />
      {feats.length > 0 && (
        <>
          <h2 className="mt-6 text-lg font-extrabold text-navy">{t("prop.features")}</h2>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5" data-testid="detail-features">
            {feats.map((f) => (
              <li key={f} className="flex items-center gap-1 text-[15px] text-navy"><Check className="h-4 w-4 text-brand" strokeWidth={3} /> {optLabel("features", f)}</li>
            ))}
          </ul>
        </>
      )}
      <ZoneMap zoneKey={listing.zone} />
      {listing.is_owner && <div className="mt-5"><SellerCard owner={listing.owner} /></div>}
      <PublishedOn iso={listing.created_at} />
    </div>
  );
}

function PublishedOn({ iso }) {
  const { t, lang } = useLang();
  return (
    <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-400">
      <CalendarDays className="h-3.5 w-3.5" /> {t("detail.published", { date: new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "es-ES") })}
    </p>
  );
}

export function GenericBody({ listing, kind }) {
  const { t, lang } = useLang();
  const { catName, optLabel } = useAppData();
  const a = listing.attributes || {};
  const chips = kind === "job"
    ? [a.contract && optLabel("contract", a.contract), a.schedule && optLabel("schedule", a.schedule), a.salary && t("job.salary", { v: a.salary })]
    : kind === "motor"
      ? [a.year && t("motor.year", { v: a.year }), a.km != null && t("motor.km", { v: Number(a.km).toLocaleString(lang === "en" ? "en-GB" : "es-ES") }), a.fuel && optLabel("fuel", a.fuel)]
      : [];
  return (
    <div data-testid="generic-detail">
      <span className="inline-block rounded-full bg-brand/15 px-3 py-1 text-sm font-semibold text-brand" data-testid="detail-category">{catName(listing.category)}</span>
      <div className="mt-3"><Head listing={listing} /></div>
      <p className="mt-2 text-3xl font-extrabold text-brand" data-testid="detail-price">{formatPrice(listing, t, lang)}</p>
      {chips.some(Boolean) && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="detail-attribute-chips">
          {chips.filter(Boolean).map((c) => <InfoChip key={c}>{c}</InfoChip>)}
        </div>
      )}
      <Description title={t("detail.description")} text={listing.description} />
      <PublishedOn iso={listing.created_at} />
      <div className="mt-5"><SellerCard owner={listing.owner} /></div>
    </div>
  );
}
